import { useState } from 'react';
import { IconJD } from './atoms';
import type { TaskItem } from '../../api/types';
import { createNote } from '../../api/client';

export interface PreppedNote {
  id: string;
  title: string;
}

export interface PersonBrief {
  person: string;
  bringUp: TaskItem[];
  promises: TaskItem[];
  prepped: PreppedNote[];
}

export interface MeetingBriefModalProps {
  meetingTitle: string;
  person: string;
  people?: PersonBrief[];
  timeLabel?: string | null;
  untilLabel?: string | null;
  bringUp: TaskItem[];
  promises: TaskItem[];
  prepped?: PreppedNote[];
  personTags?: Map<string, string>;
  onClose: () => void;
  onToggleDone: (task: TaskItem) => void;
  onOpenNote?: (noteId: string) => void;
  onNoteCreated: (noteId: string) => void;
}

function BriefRow({ task, personTag, onToggle }: { task: TaskItem; personTag?: string; onToggle: () => void }) {
  const done = task.status === 'DONE';
  return (
    <button type="button" className="jd-brief-row" onClick={onToggle}>
      <span className={`jd-brief-check ${done ? 'done' : ''}`}>
        {done && <IconJD name="check" size={10} stroke={2.4} color="#fff" />}
      </span>
      <span className={`jd-brief-row-text ${done ? 'jd-brief-text-done' : ''}`}>
        {task.title}
        {personTag && <span className="jd-brief-person-tag">{personTag}</span>}
      </span>
    </button>
  );
}

export function MeetingBriefModal({
  meetingTitle,
  person,
  people,
  timeLabel,
  untilLabel,
  bringUp,
  promises,
  prepped = [],
  personTags,
  onClose,
  onToggleDone,
  onOpenNote,
  onNoteCreated,
}: MeetingBriefModalProps) {
  const [creating, setCreating] = useState(false);

  const isMulti = people && people.length > 1;
  const totalOpen = bringUp.filter((t) => t.status !== 'DONE').length +
    promises.filter((t) => t.status !== 'DONE').length;
  const isEmpty = bringUp.length === 0 && promises.length === 0 && prepped.length === 0;

  const blurb = isMulti
    ? `Everything tied to ${people!.map((p) => p.person).join(' and ')}, gathered.`
    : isEmpty
      ? `Jotly will gather everything about ${person} here.`
      : `Everything tied to ${person}, gathered — you never filed it together.`;

  async function makeNote() {
    if (creating) return;
    setCreating(true);
    const lines = [`# ${meetingTitle}`];
    if (isMulti) {
      for (const p of people!) {
        if (p.bringUp.length || p.promises.length) {
          lines.push('', `## ${p.person}`);
          if (p.bringUp.length) {
            lines.push('', '### To bring up');
            p.bringUp.forEach((t) => lines.push(`- [ ] ${t.title}`));
          }
          if (p.promises.length) {
            lines.push('', '### I promised');
            p.promises.forEach((t) => lines.push(`- [ ] ${t.title}`));
          }
        }
      }
    } else {
      if (bringUp.length) {
        lines.push('', '## To bring up');
        bringUp.forEach((t) => lines.push(`- [ ] ${t.title}`));
      }
      if (promises.length) {
        lines.push('', '## I promised');
        promises.forEach((t) => lines.push(`- [ ] ${t.title}`));
      }
    }
    if (prepped.length) {
      lines.push('', '## Prepped');
      prepped.forEach((n) => lines.push(`- ${n.title}`));
    }
    lines.push('', '## Notes from the meeting', '');
    try {
      const note = await createNote({
        title: `${meetingTitle} — notes`,
        bodyMarkdown: lines.join('\n'),
      });
      onNoteCreated(note.id);
    } catch {
      // Stay open on failure — user can retry.
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="jd-brief-overlay" onClick={onClose}>
      <div className="jd-brief" onClick={(e) => e.stopPropagation()}>
        <div className="jd-brief-head">
          <div className="jd-rcard-eyebrow">
            <IconJD name="cal" size={11} stroke={2} color="currentColor" />
            {[timeLabel, untilLabel].filter(Boolean).join(' · ') || 'Next up'}
          </div>
          <div className="jd-brief-title">{meetingTitle}</div>
          <div className="jd-brief-blurb">{blurb}</div>
          <button type="button" className="jd-icon-btn jd-brief-close" onClick={onClose}>
            <IconJD name="x" size={15} stroke={2} color="currentColor" />
          </button>
        </div>

        <div className="jd-brief-body">
          {isEmpty ? (
            <div className="jd-brief-empty-state">
              <div className="jd-brief-empty-state-text">
                Nothing jotted about {person} yet. Jotly will gather things here as you go.
              </div>
            </div>
          ) : (
            <>
              {bringUp.length > 0 && (
                <div className="jd-brief-section">
                  <div className="jd-brief-label">
                    <IconJD name="people" size={12} stroke={1.9} color="currentColor" />
                    To bring up
                    <span className="jd-brief-count">{bringUp.length}</span>
                  </div>
                  {bringUp.map((t) => (
                    <BriefRow
                      key={t.sk}
                      task={t}
                      personTag={personTags?.get(t.sk)}
                      onToggle={() => onToggleDone(t)}
                    />
                  ))}
                </div>
              )}

              {promises.length > 0 && (
                <div className="jd-brief-section">
                  <div className="jd-brief-label jd-brief-label--warn">
                    <IconJD name="sparkle" size={12} stroke={1.9} color="currentColor" />
                    You promised
                    <span className="jd-brief-count">{promises.length}</span>
                  </div>
                  {promises.map((t) => (
                    <BriefRow
                      key={t.sk}
                      task={t}
                      personTag={personTags?.get(t.sk)}
                      onToggle={() => onToggleDone(t)}
                    />
                  ))}
                </div>
              )}

              {prepped.length > 0 && (
                <div className="jd-brief-section">
                  <div className="jd-brief-label jd-brief-label--faint">
                    <IconJD name="sparkle" size={12} stroke={1.9} color="currentColor" />
                    Prepped for you
                  </div>
                  <div className="jd-brief-prep">
                    {prepped.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        className="jd-brief-prep-row"
                        onClick={() => onOpenNote?.(n.id)}
                      >
                        <span className="jd-brief-prep-dot" />
                        <span className="jd-brief-prep-text">{n.title}</span>
                        <IconJD name="chev" size={12} color="currentColor" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="jd-brief-footnote">
                {totalOpen} open · gathered from your jots, never filed by you.
              </div>
            </>
          )}
        </div>

        <div className="jd-brief-footer">
          <button type="button" className="jd-brief-cta" onClick={makeNote} disabled={creating}>
            <IconJD name="sparkle" size={13} stroke={1.8} color="currentColor" />
            {creating ? 'Creating…' : 'Create note from this'}
          </button>
        </div>
      </div>
    </div>
  );
}
